import { calculatePlayerRating } from "./playerRating";

/** Normalized match shape for chemistry calculations */
export interface ChemistryMatch {
  teamA: number[];
  teamB: number[];
  winner?: "A" | "B" | "Draw" | null;
}

export interface DuoChemistryEntry {
  player1: number;
  player2: number;
  played: number;
  wins: number;
  winRate: number;
}

export type DuoChemistryMap = Map<string, DuoChemistryEntry>;

export interface TeamBalanceMetrics {
  teamARatingSum: number;
  teamBRatingSum: number;
  ratingDelta: number;
  teamAChemistryScore: number;
  teamBChemistryScore: number;
  chemistrySpread: number;
  isRatingImbalanced: boolean;
  isChemistryImbalanced: boolean;
  isImbalanced: boolean;
}

export interface ChemistryPairSuggestion {
  players: [number, number];
  winRate: number;
  played: number;
}

export const RATING_IMBALANCE_THRESHOLD = 15;
export const CHEMISTRY_SPREAD_THRESHOLD = 0.15;

const duoKey = (a: number, b: number): string =>
  [a, b].sort((x, y) => x - y).join("-");

export function getCombinations<T>(array: T[], k: number): T[][] {
  const result: T[][] = [];
  function combine(start: number, current: T[]) {
    if (current.length === k) {
      result.push([...current]);
      return;
    }
    for (let i = start; i < array.length; i++) {
      current.push(array[i]);
      combine(i + 1, current);
      current.pop();
    }
  }
  combine(0, []);
  return result;
}

/** Normalize DB or UI match rows to ChemistryMatch */
export function normalizeMatchForChemistry(match: Record<string, unknown>): ChemistryMatch | null {
  const teamA = (match.teamA ?? match.teama) as number[] | undefined;
  const teamB = (match.teamB ?? match.teamb) as number[] | undefined;
  if (!teamA || !teamB) return null;

  let winner = (match.winner as ChemistryMatch["winner"]) ?? null;
  if (!winner && match.scorea != null && match.scoreb != null) {
    const scoreA = match.scorea as number;
    const scoreB = match.scoreb as number;
    if (scoreA > scoreB) winner = "A";
    else if (scoreB > scoreA) winner = "B";
    else winner = "Draw";
  }
  if (!winner && match.scoreA != null && match.scoreB != null) {
    const scoreA = match.scoreA as number;
    const scoreB = match.scoreB as number;
    if (scoreA > scoreB) winner = "A";
    else if (scoreB > scoreA) winner = "B";
    else winner = "Draw";
  }

  return { teamA, teamB, winner };
}

/** Build duo win-rate map from match history */
export function calculateDuoChemistry(
  matches: Record<string, unknown>[]
): DuoChemistryMap {
  const duosMap = new Map<string, { players: [number, number]; played: number; wins: number }>();

  const recordCombo = (combo: number[], teamWon: boolean) => {
    const sorted = [...combo].sort((a, b) => a - b) as [number, number];
    const key = duoKey(sorted[0], sorted[1]);
    const existing = duosMap.get(key) ?? { players: sorted, played: 0, wins: 0 };
    existing.played++;
    if (teamWon) existing.wins++;
    duosMap.set(key, existing);
  };

  for (const raw of matches) {
    const match = normalizeMatchForChemistry(raw);
    if (!match) continue;

    const { teamA, teamB, winner } = match;

    if (teamA.length >= 2) {
      getCombinations(teamA, 2).forEach((combo) => recordCombo(combo, winner === "A"));
    }
    if (teamB.length >= 2) {
      getCombinations(teamB, 2).forEach((combo) => recordCombo(combo, winner === "B"));
    }
  }

  const result: DuoChemistryMap = new Map();
  duosMap.forEach((value, key) => {
    result.set(key, {
      player1: value.players[0],
      player2: value.players[1],
      played: value.played,
      wins: value.wins,
      winRate: value.played > 0 ? (value.wins / value.played) * 100 : 0,
    });
  });

  return result;
}

export function getDuoWinRate(
  duoMap: DuoChemistryMap,
  player1: number,
  player2: number
): number | null {
  const entry = duoMap.get(duoKey(player1, player2));
  if (!entry || entry.played === 0) return null;
  return entry.winRate / 100;
}

/** Average duo win-rate among all teammate pairs on a team (0–1 scale) */
export function calculateTeamChemistryScore(
  teamPlayerIds: number[],
  duoMap: DuoChemistryMap
): number {
  if (teamPlayerIds.length < 2) return 0.5;

  const pairs = getCombinations(teamPlayerIds, 2);
  let sum = 0;
  let count = 0;

  for (const [a, b] of pairs) {
    const rate = getDuoWinRate(duoMap, a, b);
    if (rate !== null) {
      sum += rate;
      count++;
    }
  }

  return count > 0 ? sum / count : 0.5;
}

export function getPlayerRating(player: {
  skills?: number;
  effort?: number;
  stamina?: number;
  rating?: number;
}): number {
  return player.rating ?? calculatePlayerRating(player);
}

export function calculateBalanceReport(
  teamA: { id: number; rating?: number; skills?: number; effort?: number; stamina?: number }[],
  teamB: { id: number; rating?: number; skills?: number; effort?: number; stamina?: number }[],
  matches: Record<string, unknown>[],
  options?: {
    ratingThreshold?: number;
    chemistrySpreadThreshold?: number;
  }
): TeamBalanceMetrics {
  const duoMap = calculateDuoChemistry(matches);
  const teamAIds = teamA.map((p) => p.id);
  const teamBIds = teamB.map((p) => p.id);

  const teamARatingSum = teamA.reduce((sum, p) => sum + getPlayerRating(p), 0);
  const teamBRatingSum = teamB.reduce((sum, p) => sum + getPlayerRating(p), 0);
  const ratingDelta = Math.abs(teamARatingSum - teamBRatingSum);

  const teamAChemistryScore = calculateTeamChemistryScore(teamAIds, duoMap);
  const teamBChemistryScore = calculateTeamChemistryScore(teamBIds, duoMap);
  const chemistrySpread = Math.abs(teamAChemistryScore - teamBChemistryScore);

  const ratingThreshold = options?.ratingThreshold ?? RATING_IMBALANCE_THRESHOLD;
  const chemistryThreshold =
    options?.chemistrySpreadThreshold ?? CHEMISTRY_SPREAD_THRESHOLD;

  const isRatingImbalanced = ratingDelta > ratingThreshold;
  const isChemistryImbalanced = chemistrySpread > chemistryThreshold;

  return {
    teamARatingSum,
    teamBRatingSum,
    ratingDelta,
    teamAChemistryScore,
    teamBChemistryScore,
    chemistrySpread,
    isRatingImbalanced,
    isChemistryImbalanced,
    isImbalanced: isRatingImbalanced || isChemistryImbalanced,
  };
}

/** Top duos to keep together and weak duos to split (min games filter) */
export function getChemistryPairSuggestions(
  duoMap: DuoChemistryMap,
  minGames = 3
): { keepTogether: ChemistryPairSuggestion[]; splitUp: ChemistryPairSuggestion[] } {
  const pairs: ChemistryPairSuggestion[] = [];

  duoMap.forEach((entry) => {
    if (entry.played >= minGames) {
      pairs.push({
        players: [entry.player1, entry.player2],
        winRate: entry.winRate,
        played: entry.played,
      });
    }
  });

  pairs.sort((a, b) => b.winRate - a.winRate);

  return {
    keepTogether: pairs.filter((p) => p.winRate >= 60).slice(0, 5),
    splitUp: [...pairs]
      .filter((p) => p.winRate <= 40)
      .sort((a, b) => a.winRate - b.winRate)
      .slice(0, 5),
  };
}
